import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Calendar, Wrench, Tag, Sparkles } from "lucide-react";
import UnifiedSearchBar from "@/components/UnifiedSearchBar";
import { buildSolutionsIndex, countTagsAcross, sortByRecency } from "@/lib/browse";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const publicAxios = axios.create({ baseURL: `${BACKEND}/api` });

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export default function ClosedProblemsPage() {
  const [realClosed, setRealClosed] = useState([]);
  const [realPeople, setRealPeople] = useState([]);
  const [query, setQuery] = useState("");

  useEffect(() => {
    publicAxios
      .get("/browse/closed-problems")
      .then((r) => setRealClosed(r.data || []))
      .catch(() => setRealClosed([]));
    publicAxios
      .get("/users")
      .then((r) => setRealPeople(r.data || []))
      .catch(() => setRealPeople([]));
  }, []);

  const solutions = useMemo(
    // Only surface 3 demo solutions on Answered Questions. The tool/category
    // drill-down pages (/tools/:name, /categories/:name) still see all demos
    // so those pages don't look empty.
    () => sortByRecency(buildSolutionsIndex(realClosed, 3)),
    [realClosed]
  );

  const { tools, categories } = useMemo(
    () => countTagsAcross(solutions),
    [solutions]
  );

  // Software tools are a profile-level facet (no per-solution field yet), so
  // we aggregate them across the real Directory instead of the solutions index.
  const softwareTools = useMemo(() => {
    const m = new Map();
    for (const p of realPeople) {
      for (const t of p.software_tools || []) {
        m.set(t, (m.get(t) || 0) + 1);
      }
    }
    return [...m.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, [realPeople]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return solutions;
    return solutions.filter((s) => {
      const hay = [
        s.title,
        s.summary,
        s.category,
        s.solved_by_name,
        ...(s.tools || []),
        ...(s.models || []),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [solutions, query]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-12 lg:pt-20 pb-16">
      <h1 className="font-display font-bold text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#111827]">
        Every AI problem solved on Oiiister.
      </h1>
      <p className="mt-4 text-base sm:text-lg text-[#4B5563] leading-relaxed max-w-2xl">
        Search real solutions by AI Product, category, or keyword. Learn from
        what's already worked — sign in to read any solution in full.
      </p>

      {/* Repository pill index (merged from Browse) */}
      <section className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Wrench size={16} className="text-[#3FFFB6]" />
            <h2 className="font-display font-semibold text-base text-[#111827]">
              Browse by AI Product
            </h2>
            <span className="text-xs text-[#9CA3AF]">({tools.length})</span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="browse-tools-list">
            {tools.length === 0 ? (
              <p className="text-sm text-[#9CA3AF]">No tools indexed yet.</p>
            ) : (
              tools.slice(0, 20).map((t) => (
                <Link
                  key={t.name}
                  to={`/tools/${encodeURIComponent(t.name)}`}
                  data-testid={`browse-tool-${t.name}`}
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full ring-1 ring-[#E5E7EB] bg-white text-sm font-medium text-[#111827] hover:ring-[#3FFFB6] hover:text-[#3FFFB6] transition-colors"
                >
                  {t.name}
                  <span className="text-[10px] font-semibold text-[#9CA3AF]">{t.count}</span>
                </Link>
              ))
            )}
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Tag size={16} className="text-[#3FFFB6]" />
            <h2 className="font-display font-semibold text-base text-[#111827]">
              Browse by category
            </h2>
            <span className="text-xs text-[#9CA3AF]">({categories.length})</span>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="browse-categories-list">
            {categories.length === 0 ? (
              <p className="text-sm text-[#9CA3AF]">No categories indexed yet.</p>
            ) : (
              categories.slice(0, 20).map((c) => (
                <Link
                  key={c.name}
                  to={`/categories/${encodeURIComponent(c.name)}`}
                  data-testid={`browse-category-${c.name}`}
                  className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full ring-1 ring-[#E5E7EB] bg-white text-sm font-medium text-[#111827] hover:ring-[#3FFFB6] hover:text-[#3FFFB6] transition-colors"
                >
                  {c.name}
                  <span className="text-[10px] font-semibold text-[#9CA3AF]">{c.count}</span>
                </Link>
              ))
            )}
          </div>
        </div>
      </section>

      <div className="mt-14">
        <UnifiedSearchBar
          value={query}
          onChange={setQuery}
          placeholder="Search answered questions…"
        />
      </div>

      <div className="mt-12 flex items-baseline justify-between">
        <h2 className="font-display font-semibold text-lg text-[#111827]">
          {filtered.length} answered{" "}
          {filtered.length === 1 ? "question" : "questions"}
        </h2>
      </div>

      <section
        data-testid="closed-grid"
        className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {filtered.length === 0 ? (
          <div className="col-span-full text-center py-16">
            <span className="inline-flex w-14 h-14 rounded-full bg-[#F3F4F6] text-[#9CA3AF] items-center justify-center">
              <Sparkles size={22} />
            </span>
            <p className="mt-4 text-[#4B5563]">
              Nothing here yet — try clearing filters.
            </p>
          </div>
        ) : (
          filtered.map((s) => <SolutionCard key={s.id} s={s} />)
        )}
      </section>
    </div>
  );
}

function SolutionCard({ s }) {
  const inner = (
    <>
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#F3F4F6] text-[#4B5563]">
          {s.category || "Uncategorised"}
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#065F46]">
          <CheckCircle2 size={12} /> Solved
        </span>
      </div>
      <h3 className="mt-4 font-display font-semibold text-lg text-[#111827] leading-snug">
        {s.title}
      </h3>
      <p className="mt-2 text-sm text-[#4B5563] line-clamp-3">{s.summary}</p>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {[...(s.models || []), ...(s.tools || [])].slice(0, 5).map((m) => (
          <span
            key={m}
            className="px-2 py-0.5 rounded-full text-[11px] bg-blue-50 text-blue-700"
          >
            {m}
          </span>
        ))}
      </div>
      <div className="mt-5 pt-4 border-t border-[#F3F4F6] flex items-center justify-between text-xs text-[#9CA3AF]">
        <span>By {s.solved_by_name}</span>
        <span className="inline-flex items-center gap-1">
          <Calendar size={11} /> {formatDate(s.solved_at)}
        </span>
      </div>
      {s.isDemo && (
        <p className="mt-3 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-[#F3F4F6] text-[#4B5563]">
          Demo
        </p>
      )}
    </>
  );

  const cls =
    "block bg-white rounded-3xl p-6 ring-1 ring-[#F3F4F6] hover:ring-[#D1FAE5] card-lift";

  if (s.problem_id) {
    return (
      <Link
        to={`/problems/${s.problem_id}`}
        data-testid={`closed-${s.problem_id}`}
        className={cls}
      >
        {inner}
      </Link>
    );
  }
  if (s.solved_by_slug) {
    return (
      <Link
        to={`/people/${s.solved_by_slug}`}
        data-testid={`closed-mock-${s.id}`}
        className={cls}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div data-testid={`closed-${s.id}`} className={cls}>
      {inner}
    </div>
  );
}
